<template>
  <div class="InvitationsView container">
    <h1
      class="WelcomeView__heading flex items-center font-family-display font-weight-bold mb-16 flex"
    >
      <router-link
        :to="{ name: 'WelcomeView' }"
        class="color-black font-size-xxl mr-16"
      >
        <span class="icon-simple-arrow-left"></span>
      </router-link>
      {{ $t('my_invitations') }}
    </h1>
    <p class="flex color-gray-3 mb-48">
      <span class="icon-circle-alert mr-8"></span>
      {{ $t('my_invitations_subtitle') }}
    </p>

    <ul class="InvitationsView__list">
      <li
        v-for="invitation in invitations"
        :key="invitation.id"
        class="InvitationsView__list-card"
      >
        <div class="InvitationsView__list-img">
          <div
            class="InvitationsView__list-img-placeholder font-family-display font-weight-medium"
          >
            <span>
              {{ picPlaceholder(invitation.stage.title) }}
            </span>
          </div>
        </div>
        <div class="InvitationsView__list-info">
          <div class="InvitationsView__list-info-text">
            <h2>{{ invitation.stage.fqdn }}</h2>
            <p class="color-gray-3">
              {{ invitation.inviter.firstName }}
              {{ invitation.inviter.lastName }}
            </p>
          </div>
          <div class="InvitationsView__list-info-actions">
            <button
              class="InvitationsView__list-info-actions-accept"
              @click="acceptInvitation(invitation.id)"
            >
              <div>
                <span class="icon-check-sign color-succe"></span>
              </div>
              <small>Accept</small>
            </button>
            <button
              class="InvitationsView__list-info-actions-reject"
              @click="rejectInvitation(invitation.id, invitation.stage.title)"
            >
              <div>
                <span class="icon-close"></span>
              </div>
              <small>Reject</small>
            </button>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import { useInvitationsStore } from '@/stores/invitationsStore'
import { useModalStore } from '@/stores/modalStore'
import { ModalTextComponent } from '@snowshoe-frontend/modal-component'
import { storeToRefs } from 'pinia'

const invitationsStore = useInvitationsStore()
const { invitations } = storeToRefs(invitationsStore)
const { createModal } = useModalStore()

function picPlaceholder(title) {
  const initials = title.includes(' ')
    ? `${title.split('-')[0][0]} ${title.split(' ')[1][0]}`
    : title[0]

  return initials
}

async function acceptInvitation(invitationId) {
  await invitationsStore.acceptInvitation(invitationId)
}

async function rejectInvitation(invitationId, stageTitle) {
  await createModal({
    component: ModalTextComponent,
    props: {
      leftIconClass: 'icon-circle-alert',
      title: 'Are you sure?',
      bodyText: `You are about to reject invitation to ${stageTitle}`,
      saveText: 'Yes, reject'
    }
  })

  await invitationsStore.rejectInvitation(invitationId)
}

onMounted(async () => {
  await invitationsStore.getInvitations()
})
</script>

<style lang="scss" scoped>
.InvitationsView {
  &__list {
    &-card {
      background-color: var(--color-secondary);
      border-radius: rem(16);
      box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.04);
      overflow: hidden;
      position: relative;
      display: flex;
      flex-direction: column;
      margin-bottom: 40px;

      @include tablet {
        flex-direction: row;
        height: 83px;
        margin-bottom: var(--grid-24);
      }
    }

    &-img {
      width: 100%;
      height: rem(210);
      position: relative;

      @include tablet {
        width: 120px;
        height: 100%;
      }

      &-placeholder {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        width: 100%;
        height: 100%;
        background-color: #7143ae;
        color: #eff0f6;
        letter-spacing: -3px;
        font-size: rem(80);

        @include tablet {
          font-size: rem(40);
        }
      }
    }

    &-info {
      padding: rem(16);
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: 100%;

      @include tablet {
        flex-direction: row;
        padding: rem(8) rem(16);
      }

      &-text {
        & > h2 {
          color: var(--color-primary);
          font-family: var(--font-family-primary);
          font-weight: var(--font-weight-semibold);
          font-size: rem(16);
          display: block;

          @include desktop {
            font-size: rem(20);
          }
        }
      }

      &-actions {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: var(--grid-24);
        padding-top: var(--grid-24);
        border-top: 1px solid #c9c9c9;

        @include tablet {
          margin-top: initial;
          padding-top: initial;
          border-top: none;
        }

        & > button {
          & > div {
            font-size: 16px;
            width: 40px;
            height: 40px;
            border-radius: 100%;
            border: 2px solid;
            cursor: pointer;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: var(--grid-4);

            &:hover {
              opacity: 0.7;
            }
          }

          & > small {
            text-transform: uppercase;
          }

          &:not(:last-child) {
            margin-right: 80px;

            @include tablet {
              margin-right: 16px;
            }
          }
        }

        &-accept {
          color: var(--color-success-text);
        }

        &-reject {
          color: var(--color-gray-3);
        }
      }
    }
  }
}
</style>
